<route lang="yaml">
meta:
  public: true
</route>

<script setup lang="ts">
import { useAuth } from '@/auth'
import LanguageSelector from '@/components/LanguageSelector.vue'
import LoadingPage from '@/components/loading/LoadingPage.vue'
import { IS_BAYER } from '@/config/customers'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

let auth = useAuth()
const router = useRouter()
const { t } = useI18n()
const showLoadingWhileRedirecting = ref(false)
const SHOW_BLANK_PAGE_ON_HOME_REDIRECTION = window.ENV.SHOW_BLANK_PAGE_ON_HOME_REDIRECTION

onMounted(() => {
  if (!IS_BAYER) {
    /*
    We redirect to dashboard page, if the user is authenticated he can see the home page, else he will be redirected
    automatically to the provider login page
    */
    showLoadingWhileRedirecting.value = true
    // NOTE: We want to remove the current index (landing) page, because it is unnecessary, so, we going to redirect the user to dashboard when the user set this URL path
    router.push({
      name: 'dashboard',
    })
  }
})

function callToAction() {
  auth.isAuthenticated ? router.push({ name: 'home' }) : auth.signup()
}

function login() {
  auth.loginWithRedirect()
}
</script>

<template>
  <div v-if="!showLoadingWhileRedirecting" class="landing-page min-vh-100">
    <nav class="navbar sticky-top navbar-dark text-white navbar-expand-md border-bottom">
      <div class="container-xl">
        <div class="d-flex">
          <a class="navbar-brand d-flex align-items-center text-white" href="#">
            <img src="/logo.svg" height="40" />
            <img
              v-if="IS_BAYER"
              src="https://www.bayer.com/themes/custom/bayer_cpa/logo.svg"
              alt="Bayer"
              height="40"
              class="ms-4"
            />
          </a>

          <div class="d-flex align-items-center">
            <ul v-if="auth.isAuthenticated" class="navbar-nav">
              <li class="nav-item">
                <router-link class="nav-link active text-primary" aria-current="page" :to="{ name: 'home' }">
                  {{ t('To dashboard') }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <button
          class="navbar-toggler text-primary fs-1 shadow-none pe-0"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarCollapse"
        >
          <i-mdi-menu></i-mdi-menu>
        </button>

        <div id="navbarCollapse" class="collapse navbar-collapse flex-grow-0">
          <ul class="navbar-nav align-items-center">
            <LanguageSelector></LanguageSelector>

            <li v-if="auth.isAuthenticated" class="nav-item dropdown">
              <a
                class="nav-link dropdown-toggle d-flex align-items-center text-primary"
                href="#"
                data-bs-toggle="dropdown"
              >
                <img :src="auth.user?.picture" class="rounded-circle" height="30" />
              </a>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <small class="dropdown-item-text text-muted">{{ auth.user?.nickname }}</small>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="$auth.logout">{{ t('Logout') }}</a>
                </li>
              </ul>
            </li>

            <li v-else class="nav-item d-flex">
              <a class="nav-link text-primary ms-3" href="#" @click.prevent="login()">
                {{ t('Login') }}
              </a>

              <a class="nav-link text-primary ms-3" href="#" @click.prevent="$auth.signup()">
                {{ t('Register') }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <div class="container-xl">
      <div class="main-section text-white mt-100 pt-6">
        <h1 class="display-4 text-info">{{ t('AI Driven Regulatory Compliance') }}<br /></h1>
        <p v-if="IS_BAYER" class="fs-5 text-info">
          Support for SDS Compilers, raw material SDS recipients
          <br />
          Use AI and automation to support business tasks
          <br />
          Reduce manual and repetitive tasks
          <br />
          Upload data to SAP (upcoming)
        </p>
        <p v-else class="fs-5 text-info">
          {{ t('Support employees to automate repetitive, manual tasks') }}
          <br />
          {{ t('Simply merge business data to safety information') }}
          <br />
          {{ t('Upgrade old IT applications using AI and automation') }}
        </p>

        <div>
          <button class="btn btn-lg btn-success text-white mt-5 rounded-pill" @click="callToAction">
            <span v-if="IS_BAYER">To request access, please raise a ticket in IT Service Portal</span>
            <span v-else>{{ t('Get access for free') }}</span>
            <i-mi-arrow-right class="ms-2 fs-5 text-success-darken"></i-mi-arrow-right>
          </button>
        </div>
        <button class="btn btn-lg btn-success text-white mt-2 text-capitalize rounded-pill" @click="callToAction">
          {{ t('Start uploading SDS now') }}
          <i-bi-file-earmark-fill class="ms-2 fs-5 text-success-darken"></i-bi-file-earmark-fill>
        </button>
      </div>
    </div>
  </div>
  <div v-else-if="SHOW_BLANK_PAGE_ON_HOME_REDIRECTION"></div>
  <LoadingPage v-else />
</template>

<style lang="scss">
@import '@/assets/scss/variables';

.landing-page {
  background: linear-gradient(180deg, #ffffffbf 0%, rgba(78, 166, 225, 0) 100%), #4ea6e159;

  .main-section {
    max-width: 700px;
  }
}
</style>
